import React, { useState, useEffect, useRef, useCallback } from 'react';
import { FileText, Download, X, Eye } from 'lucide-react'; 
import { renderDocument } from '../services/api';

const styles = {
    modalOverlay: {
        position: 'fixed',
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        backgroundColor: 'rgba(0, 0, 0, 0.7)',
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
        zIndex: 1000,
    },
    modalContent: {
        backgroundColor: '#ffffff',
        borderRadius: '8px',
        boxShadow: '0 4px 10px rgba(0, 0, 0, 0.3)',
        display: 'flex',
        flexDirection: 'column',
        minWidth: '95%',
        width: '95%',
        height: '95vh',
        maxWidth: '1400px',
    },
    modalHeader: {
        padding: '15px 20px',
        borderBottom: '1px solid #e0e0e0',
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
    },
    modalHeaderH2: {
        margin: 0,
        fontSize: '1.25rem', // Reduced slightly for better mobile fit
        color: '#333',
        whiteSpace: 'nowrap',
        overflow: 'hidden',
        textOverflow: 'ellipsis',
        maxWidth: '70%', // Prevents title from pushing buttons off screen
    },
    closeButton: {
        background: 'none',
        border: 'none',
        fontSize: '1.5rem',
        cursor: 'pointer',
        color: '#6c757d',
    },
    modalFooter: {
        padding: '10px 20px',
        borderTop: '1px solid #e0e0e0',
        display: 'flex',
        justifyContent: 'flex-end',
        alignItems: 'center',
    },
    footerNav: {
        display: 'flex',
        gap: '10px',
    },
    buttonSecondary: {
        padding: '8px 15px',
        backgroundColor: '#6c757d',
        color: 'white',
        border: 'none',
        borderRadius: '4px',
        cursor: 'pointer',
        fontSize: '0.9rem',
    },
    completeButton: {
        padding: '8px 15px',
        backgroundColor: '#28a745', 
        color: 'white',
        border: 'none',
        borderRadius: '4px',
        cursor: 'pointer',
        fontSize: '0.9rem',
        fontWeight: '500',
    },
    downloadButton: {
        padding: '8px 15px',
        backgroundColor: '#007bff',
        color: 'white',
        border: 'none',
        borderRadius: '4px',
        cursor: 'pointer',
        fontSize: '0.9rem',
        fontWeight: '500',
        transition: 'background-color 0.2s',
    },
    nextButton: { // NEW STYLE
        padding: '8px 15px',
        backgroundColor: '#28a745', // Green color
        color: 'white',
        border: 'none',
        borderRadius: '4px',
        cursor: 'pointer',
        fontSize: '0.9rem',
        fontWeight: '500',
        transition: 'background-color 0.2s',
    },
    pdfModalBody: {
        flexGrow: 1,
        display: 'flex',
        padding: '0',
        overflow: 'hidden', // Ensures content stays within bounds
    },
    pdfSidebar: {
        width: '200px',
        padding: '15px',
        borderRight: '1px solid #e0e0e0',
        backgroundColor: '#f4f5f7',
        flexShrink: 0,
        overflowY: 'auto',
    },
    pdfViewerArea: {
        flexGrow: 1,
        display: 'flex',
        flexDirection: 'column',
    },
    pdfToolbar: {
        padding: '10px 20px',
        borderBottom: '1px solid #e0e0e0',
        backgroundColor: '#ffffff',
        display: 'flex',
        alignItems: 'center',
        gap: '15px',
        fontSize: '0.9rem',
    },
    pdfFrameWrapper: {
        flexGrow: 1,
        position: 'relative',
        padding: '20px',
        backgroundColor: '#e9ecef',
    },
    loadingOverlay: {
        position: 'absolute',
        top: '50%',
        left: '50%',
        transform: 'translate(-50%, -50%)',
        color: '#6c757d',
        fontSize: '1.1rem',
        zIndex: 10,
        backgroundColor: 'rgba(255, 255, 255, 0.8)',
        padding: '15px',
        borderRadius: '5px',
        textAlign: 'center',
    },
    thumbnailPlaceholder: {
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        marginTop: '10px',
    },
    thumbnailStyle: {
        width: '100px',
        height: '140px',
        border: '2px solid #004d99',
        backgroundColor: '#ffffff',
        borderRadius: '4px',
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        marginTop: '10px',
        cursor: 'pointer',
    },
};

const DocumentViewer = ({ isOpen, onClose, CaseID, documentType, onFinishProcess, onNext, hasNext }) => {
    const iframeRef = useRef(null);
    const [pdfReady, setPdfReady] = useState(false);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(null);
    const [pdfUrl, setPdfUrl] = useState('');
    const [pdfFilename, setPdfFilename] = useState('');
    
    // 1. Add state to track screen width
    const [isMobile, setIsMobile] = useState(window.innerWidth < 768);

    const documentTitle = documentType.replace(/([A-Z])/g, ' $1').trim();

    // 2. Add resize listener to update mobile state
    useEffect(() => {
        const handleResize = () => setIsMobile(window.innerWidth < 768);
        window.addEventListener('resize', handleResize);
        return () => window.removeEventListener('resize', handleResize);
    }, []);

    const generateAndLoadPdf = useCallback(async () => {
        if (!CaseID || !documentType) return;
        setLoading(true);
        setPdfReady(false);
        setError(null);
        
        try {
            const response = await renderDocument({ CaseID, documentType });
            const result = response.data || response;

            if (!result || !result.pdfFile) {
                throw new Error('API response did not contain the expected PDF filename.');
            }
            
            const filename = result.pdfFile;
            const directDownloadPath = `${process.env.REACT_APP_API_URL}/cases/getDoc/view/${filename}`;

            setPdfFilename(filename);
            setPdfUrl(directDownloadPath);

            if (iframeRef.current) {
                iframeRef.current.src = directDownloadPath;
            }

        } catch (err) {
            console.error('Document generation error:', err);
            const errorMessage = err.response?.data?.message || err.message || 'An unexpected error occurred.';
            setError(errorMessage);
        } finally {
            setLoading(false);
        }
    }, [CaseID, documentType]);

    useEffect(() => {
        if (isOpen) {
            generateAndLoadPdf();
        } else {
            setPdfUrl('');
            setPdfFilename('');
            setPdfReady(false);
            setError(null);
        }
    }, [isOpen, generateAndLoadPdf]);

    const downloadPdf = () => {
        if (pdfUrl) {
            const link = document.createElement('a');
            link.href = pdfUrl;
            link.download = pdfFilename || `${documentTitle}_${CaseID}.pdf`;
            document.body.appendChild(link);
            link.click();
            document.body.removeChild(link);
        }
    };

    if (!isOpen) return null;

    const isLoadingOrError = loading || error;
    const finishButtonLoading = onFinishProcess && loading;

    return (
        <div style={styles.modalOverlay} onClick={onClose}>
            <div style={styles.modalContent} onClick={e => e.stopPropagation()}>
                <div style={styles.modalHeader}>
                    <h2 style={styles.modalHeaderH2}>
                        <FileText size={20} style={{ marginRight: '10px', verticalAlign: 'middle' }} />
                        {/* Truncate long titles on mobile if needed via CSS styles */}
                        {documentTitle} Preview
                    </h2>
                    <div style={{ display: 'flex', gap: '10px' }}>
                        {/* Hide text label on mobile, keep icon */}
                        <button onClick={downloadPdf} style={styles.downloadButton} disabled={!pdfReady || isLoadingOrError}>
                            <Download size={18} style={{ verticalAlign: 'middle', marginRight: !isMobile ? '5px' : '0' }} /> 
                            {!isMobile && 'Download PDF'}
                        </button>
                        <button onClick={onClose} style={styles.closeButton} title="Close Modal">&times;</button>
                    </div>
                </div>

                <div style={styles.pdfModalBody}>
                    {/* 3. Conditionally render Sidebar only if NOT mobile */}
                    {!isMobile && (
                        <div style={styles.pdfSidebar}>
                            <h4>Document Thumbnails</h4>
                            <div style={styles.thumbnailPlaceholder}>
                                {pdfReady ? (
                                    <p>Page 1 of 1</p>
                                ) : (
                                    <p>{error ? 'Error' : 'Generating PDF...'}</p>
                                )}
                                <div style={{ ...styles.thumbnailStyle, borderColor: pdfReady ? '#004d99' : '#ccc' }}>
                                    <Eye size={20} color={pdfReady ? '#004d99' : '#ccc'} />
                                    <p style={{ margin: 0, fontSize: '0.9rem', color: pdfReady ? '#333' : '#ccc' }}>1</p>
                                </div>
                            </div>
                        </div>
                    )}

                    <div style={styles.pdfViewerArea}>
                        <div style={styles.pdfToolbar}>
                            <span style={{ color: '#6c757d' }}>{pdfReady ? '1 / 1' : 'Generating...'}</span>
                            <span style={{ color: '#6c757d', marginLeft: '20px' }}>100%</span>
                        </div>

                        <div style={styles.pdfFrameWrapper}>
                            {(loading || error) && (
                                <p style={styles.loadingOverlay}>
                                    {loading ? 'Loading PDF Preview...' : `Error: ${error}`}
                                </p>
                            )}
                            <iframe
                                ref={iframeRef}
                                width="100%"
                                height="100%"
                                style={{
                                    border: 'none',
                                    backgroundColor: '#e9ecef',
                                    visibility: pdfReady && !error ? 'visible' : 'hidden',
                                }}
                                title={`${documentTitle} Preview`}
                                src={pdfUrl}
                                onLoad={() => setPdfReady(true)}
                                onError={() => {
                                    setPdfReady(false);
                                    if (!error) setError('Failed to load PDF in viewer.');
                                }}
                            />
                        </div>
                    </div>
                </div>

                <div style={styles.modalFooter}>
                    <div style={styles.footerNav}>
                        <button onClick={onClose} style={styles.buttonSecondary} disabled={finishButtonLoading}>Cancel</button>
                        {onFinishProcess && (
                            <button
                                onClick={onFinishProcess}
                                disabled={finishButtonLoading || !pdfReady || !!error}
                                style={styles.completeButton}
                            >
                                {finishButtonLoading ? 'Finishing...' : 'Finish Process'}
                            </button>
                        )}
                        {hasNext && (
                            <button
                                onClick={onNext}
                                disabled={loading || !pdfReady || !!error}
                                style={styles.nextButton} // Using new style
                            >
                                Next Document
                            </button>
                        )}
                    </div>
                </div>
            </div>
        </div>
    );
};

export default DocumentViewer;